import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/AuthContext';

interface HeaderProps {
  onLogin: () => void;
  onRegister: () => void;
}

const Header: React.FC<HeaderProps> = ({ onLogin, onRegister }) => {
  const { isAuthenticated, user, logout, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <header className='bg-primary border-b border-white/20 sticky top-0 z-50'>
      <div className='max-w-7xl mx-auto px-2 sm:px-4 lg:px-8'>
        <div className='flex justify-between items-center py-2 sm:py-4'>
          {/* Logo and Title */}
          <div className='flex items-center flex-shrink-0'>
            <Link to="/" className="flex items-center" onClick={closeMobileMenu}>
              <img 
                src='/logo.png.jpg' 
                alt='CricFantasyPros Logo' 
                className='h-10 w-10 sm:h-16 sm:w-16 md:h-20 md:w-20 lg:h-24 lg:w-24 mr-2 sm:mr-4 md:mr-6 rounded-full shadow-md' 
              />
              <div>
                <h1 className='text-sm sm:text-lg md:text-2xl lg:text-3xl xl:text-4xl font-extrabold text-white leading-tight'>
                  CricFantasyPros
                </h1>
                <span className='block text-accent text-[10px] sm:text-xs md:text-sm lg:text-base xl:text-lg font-semibold tracking-wide -mt-0.5 sm:-mt-1'>
                  Live the game you love
                </span>
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className='hidden md:flex space-x-4 lg:space-x-6'>
            <Link 
              to="/" 
              className={`transition-colors text-lg font-semibold ${
                isActive('/') ? 'text-accent' : 'text-secondary hover:text-accent'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/tournaments" 
              className={`transition-colors text-lg font-semibold ${
                isActive('/tournaments') ? 'text-accent' : 'text-secondary hover:text-accent'
              }`}
            >
              LIVE
            </Link>
            {isAuthenticated && (
              <>
                <Link 
                  to="/my-leagues" 
                  className={`transition-colors text-lg font-semibold ${
                    isActive('/my-leagues') ? 'text-accent' : 'text-secondary hover:text-accent'
                  }`}
                >
                  Leagues
                </Link>
                <Link 
                  to="/profile" 
                  className={`transition-colors text-lg font-semibold ${
                    isActive('/profile') ? 'text-accent' : 'text-secondary hover:text-accent'
                  }`}
                >
                  Profile
                </Link>
                {/* Admin link only for admin users */}
                {user?.is_admin && (
                  <Link
                    to="/admin"
                    className={`transition-colors text-lg font-semibold ${
                      isActive('/admin') ? 'text-accent' : 'text-secondary hover:text-accent'
                    }`}
                  >
                    Admin
                  </Link>
                )}
              </>
            )}
          </nav>

          {/* Desktop Auth Buttons */}
          <div className='hidden md:flex items-center space-x-2 lg:space-x-4'>
            {isAuthenticated ? (
              <div className='flex flex-col items-end space-y-1'>
                <button onClick={logout} className='bg-accent hover:bg-secondary text-primary font-semibold px-3 lg:px-4 py-2 rounded-lg transition-colors text-sm'>
                  Logout
                </button>
                <span className='text-white/80 text-xs truncate max-w-[120px] lg:max-w-none'>
                  Welcome, {user?.display_name || user?.email}
                </span>
              </div>
            ) : (
              <>
                <button onClick={onLogin} className='bg-accent hover:bg-secondary text-primary font-semibold px-3 lg:px-4 py-2 rounded-lg transition-colors text-sm lg:text-base'>
                  Login
                </button>
                <button onClick={onRegister} className='bg-secondary hover:bg-accent text-primary font-semibold px-3 lg:px-4 py-2 rounded-lg transition-colors text-sm lg:text-base'>
                  Sign Up
                </button>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className='md:hidden p-2 text-white hover:text-accent transition-colors'
            aria-label='Toggle menu'
          >
            <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              {mobileMenuOpen ? (
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
              ) : (
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 12h16M4 18h16' />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className='md:hidden border-t border-white/20 py-4'>
            <nav className='flex flex-col space-y-4'>
              <Link 
                to="/" 
                onClick={closeMobileMenu}
                className={`transition-colors text-base font-semibold py-2 ${
                  isActive('/') ? 'text-accent' : 'text-secondary hover:text-accent'
                }`}
              >
                Home
              </Link>
              <Link 
                to="/tournaments" 
                onClick={closeMobileMenu}
                className={`transition-colors text-base font-semibold py-2 ${
                  isActive('/tournaments') ? 'text-accent' : 'text-secondary hover:text-accent'
                }`}
              >
                LIVE
              </Link>
              {isAuthenticated && (
                <>
                  <Link 
                    to="/my-leagues" 
                    onClick={closeMobileMenu}
                    className={`transition-colors text-base font-semibold py-2 ${
                      isActive('/my-leagues') ? 'text-accent' : 'text-secondary hover:text-accent'
                    }`}
                  >
                    Leagues
                  </Link>
                  <Link 
                    to="/profile" 
                    onClick={closeMobileMenu}
                    className={`transition-colors text-base font-semibold py-2 ${
                      isActive('/profile') ? 'text-accent' : 'text-secondary hover:text-accent'
                    }`}
                  >
                    Profile
                  </Link>
                  {user?.is_admin && (
                    <Link
                      to="/admin"
                      onClick={closeMobileMenu}
                      className={`transition-colors text-base font-semibold py-2 ${
                        isActive('/admin') ? 'text-accent' : 'text-secondary hover:text-accent'
                      }`}
                    >
                      Admin
                    </Link>
                  )}
                </>
              )}

              {/* Mobile Auth Buttons */}
              <div className='pt-4 border-t border-white/20 flex flex-col space-y-2'>
                {isAuthenticated ? (
                  <>
                    <div className='text-white/80 text-sm mb-2'>
                      Welcome, {user?.display_name || user?.email}
                    </div>
                    <button 
                      onClick={() => { logout(); closeMobileMenu(); }} 
                      className='bg-accent hover:bg-secondary text-primary font-semibold px-4 py-2 rounded-lg transition-colors w-full text-center'
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={() => { onLogin(); closeMobileMenu(); }} 
                      className='bg-accent hover:bg-secondary text-primary font-semibold px-4 py-2 rounded-lg transition-colors w-full text-center'
                    >
                      Login
                    </button>
                    <button 
                      onClick={() => { onRegister(); closeMobileMenu(); }} 
                      className='bg-secondary hover:bg-accent text-primary font-semibold px-4 py-2 rounded-lg transition-colors w-full text-center'
                    >
                      Sign Up
                    </button>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
